/**
 * Confirmation Page
 * Review payment details before submitting to Cybersource
 * Billing info will be collected on Cybersource's secure page
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { usePaymentStore } from '@/store/paymentStore';
import { preparePayment } from '@/api/services/paymentService';
import { Button, LoadingSpinner, ErrorMessage, Card, CardBody } from '@/components/common';
import { PaymentSummary, CybersourceForm } from '@/components/confirmation';
import { ROUTE_PATHS, ERROR_MESSAGES } from '@/utils/constants';

export default function ConfirmationPage() {
  const navigate = useNavigate();
  const { balance, paymentAmount, setCybersourceData } = usePaymentStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldSubmitToCybersource, setShouldSubmitToCybersource] = useState(false);
  const [cybersourceUrl, setCybersourceUrl] = useState<string>('');
  const [cybersourceData, setCybersourceFormData] = useState<Record<string, string>>({});

  // Redirect if no payment amount or balance
  useEffect(() => {
    if (!paymentAmount || !balance) {
      navigate(ROUTE_PATHS.BALANCE, { replace: true });
    }
  }, [paymentAmount, balance, navigate]);

  const handleContinueToPayment = async () => {
    if (!paymentAmount) {
      setError('Payment amount is missing. Please go back and enter a payment amount.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare payment with backend
      const prepareResponse = await preparePayment(paymentAmount);

      // Store Cybersource data
      setCybersourceData(prepareResponse);
      setCybersourceUrl(prepareResponse.cybersourceUrl);
      setCybersourceFormData(prepareResponse.formData);

      // Trigger form submission to Cybersource
      setShouldSubmitToCybersource(true);
    } catch (err) {
      console.error('Failed to prepare payment:', err);
      setIsSubmitting(false);

      if (err instanceof AxiosError) {
        if (err.response?.status === 401) {
          // Unauthorized - redirect to login
          navigate(ROUTE_PATHS.LOGIN, { replace: true });
        } else if (err.response?.status === 400) {
          setError('Invalid payment information. Please check your details and try again.');
        } else if (err.response?.status === 500) {
          setError(ERROR_MESSAGES.GENERIC);
        } else {
          setError('Failed to prepare payment. Please try again.');
        }
      } else {
        setError(ERROR_MESSAGES.NETWORK);
      }
    }
  };

  // Loading state while checking balance/payment
  if (!balance || !paymentAmount) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <LoadingSpinner size="lg" message="Loading payment details..." />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back Navigation */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTE_PATHS.BALANCE)}
          disabled={isSubmitting}
          size="sm"
        >
          &larr; Back to Balance
        </Button>
      </div>

      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-semibold text-text">Payment Confirmation</h1>
        <p className="text-sm text-text-muted mt-1">
          Review your payment details before continuing to secure checkout
        </p>
      </div>

      {/* Error Message */}
      {error && <ErrorMessage message={error} variant="banner" />}

      {/* Information Notice */}
      <Card variant="warning">
        <CardBody>
          <div className="flex items-start gap-3">
            <svg
              className="h-5 w-5 text-warning flex-shrink-0 mt-0.5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-sm font-semibold text-text">Important Notice</p>
              <p className="text-sm text-text-muted mt-1">
                You will be redirected to{' '}
                <span className="font-semibold">CyberSource</span>, a secure third-party payment
                processor, to complete your payment. Your card information will not be stored by
                RSUI.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Payment Summary */}
      <PaymentSummary balance={balance} paymentAmount={paymentAmount} />

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTE_PATHS.BALANCE)}
          disabled={isSubmitting}
        >
          &larr; Back
        </Button>
        <Button
          variant="primary"
          onClick={handleContinueToPayment}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Preparing...' : 'Pay Bill'}
        </Button>
      </div>

      {/* Hidden Cybersource Form - Auto-submits when ready */}
      {shouldSubmitToCybersource && cybersourceUrl && (
        <CybersourceForm
          cybersourceUrl={cybersourceUrl}
          cybersourceData={cybersourceData}
          shouldSubmit={shouldSubmitToCybersource}
        />
      )}
    </div>
  );
}
